import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Plus, Menu, X, User, LogOut, Settings, MessageSquare, Heart, Loader2, ShoppingBag, BarChart3, MapPin, Mic, MicOff, Bell, Bookmark, Users, LocateFixed } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { useCountry } from '../context/CountryContext';
// CountrySelector hidden from header - country auto-detected by IP.
// import CountrySelector from './CountrySelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const { formatCurrency } = useCountry();
  const { country } = useCountry();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingLocSuggestions, setLoadingLocSuggestions] = useState(false);
  // "Near me" geolocation state - when set, search uses lat/lng + radius_km instead of text location
  const [nearMe, setNearMe] = useState(null); // { lat, lng, radius_km }
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [categories, setCategories] = useState([]);
  
  // Voice search states
  const [isListening, setIsListening] = useState(false);
  const [isListeningLocation, setIsListeningLocation] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);
  const locationRecognitionRef = useRef(null);
  const autoSearchTimeoutRef = useRef(null);
  
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const locationRef = useRef(null);
  const debounceTimer = useRef(null);
  const locationDebounceTimer = useRef(null);
  
  // Sri Lanka locations for suggestions
  const sriLankaLocations = [
    'Colombo', 'Kandy', 'Galle', 'Jaffna', 'Negombo', 'Batticaloa', 
    'Trincomalee', 'Anuradhapura', 'Ratnapura', 'Badulla', 'Matara',
    'Kurunegala', 'Gampaha', 'Kalutara', 'Nuwara Eliya', 'Hambantota',
    'Polonnaruwa', 'Ampara', 'Mannar', 'Vavuniya', 'Kilinochchi',
    'Mullativu', 'Monaragala', 'Kegalle', 'Puttalam', 'Matale'
  ];
  
  // User auth state
  const isLoggedIn = localStorage.getItem('user');
  const user = isLoggedIn ? JSON.parse(isLoggedIn) : null;
  const token = localStorage.getItem('token');

  // Get recognition language based on saved preference
  const getRecognitionLang = () => {
    const savedLang = localStorage.getItem('zeilian_language');
    if (savedLang === 'si') return 'si-LK';
    if (savedLang === 'ta') return 'ta-LK';
    if (savedLang === 'zh-CN') return 'zh-CN';
    if (savedLang === 'hi') return 'hi-IN';
    return 'en-US';
  };

  // Initialize Speech Recognition for Search
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setVoiceSupported(true);
      
      // Search field recognition
      const searchRecognition = new SpeechRecognition();
      searchRecognition.continuous = false;
      searchRecognition.interimResults = true;
      
      searchRecognition.onstart = () => setIsListening(true);
      
      searchRecognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setSearchQuery(transcript);
      };
      
      searchRecognition.onend = () => {
        setIsListening(false);
        // Auto-search after voice input ends (with small delay)
        if (autoSearchTimeoutRef.current) clearTimeout(autoSearchTimeoutRef.current);
        autoSearchTimeoutRef.current = setTimeout(() => {
          // Trigger search if we have a query
          const form = document.querySelector('form[data-search-form]');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }, 500);
      };
      
      searchRecognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognitionRef.current = searchRecognition;

      // Location field recognition
      const locationRecognition = new SpeechRecognition();
      locationRecognition.continuous = false;
      locationRecognition.interimResults = true;
      
      locationRecognition.onstart = () => setIsListeningLocation(true);
      
      locationRecognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setLocationQuery(transcript);
      };
      
      locationRecognition.onend = () => {
        setIsListeningLocation(false);
      };
      
      locationRecognition.onerror = (event) => {
        console.error('Location recognition error:', event.error);
        setIsListeningLocation(false);
      };
      
      locationRecognitionRef.current = locationRecognition;
    }
    
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
      if (locationRecognitionRef.current) locationRecognitionRef.current.abort();
      if (autoSearchTimeoutRef.current) clearTimeout(autoSearchTimeoutRef.current);
    };
  }, []);

  // Toggle voice search for main search field
  const toggleVoiceSearch = () => {
    if (!voiceSupported) {
      alert('Voice search is not supported in your browser. Please try Chrome or Edge.');
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      // Stop location recognition if active
      if (isListeningLocation) locationRecognitionRef.current?.stop();
      
      if (recognitionRef.current) {
        recognitionRef.current.lang = getRecognitionLang();
        recognitionRef.current.start();
      }
    }
  };

  // Toggle voice input for location field
  const toggleLocationVoice = () => {
    if (!voiceSupported) {
      alert('Voice input is not supported in your browser. Please try Chrome or Edge.');
      return;
    }
    
    if (isListeningLocation) {
      locationRecognitionRef.current?.stop();
    } else {
      // Stop search recognition if active
      if (isListening) recognitionRef.current?.stop();
      
      if (locationRecognitionRef.current) {
        locationRecognitionRef.current.lang = getRecognitionLang();
        locationRecognitionRef.current.start();
      }
    }
  };

  // Fetch categories for suggestions — scoped to current visitor country so
  // the search-bar suggestions and counts reflect the site the user is on.
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const params = country ? `?country_code=${country}` : '';
        const response = await axios.get(`${API_URL}/api/listings/homepage-data${params}`);
        if (response.data.categories) {
          setCategories(response.data.categories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [country]);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/chat/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadMessages(response.data.unread_count);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    
    fetchUnreadCount();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search suggestions (items + categories)
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchQuery.length < 2) {
      setSuggestions([]);
      setCategorySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        // Search listings - pass country_code for isolation
        const cParam = country ? `&country_code=${country}` : '';
        const response = await fetch(
          `${API_URL}/api/listings/?search=${encodeURIComponent(searchQuery)}&limit=4${cParam}`
        );
        const data = await response.json();
        
        if (data.listings) {
          setSuggestions(data.listings);
        }

        // Filter categories that match
        const matchingCategories = [];
        categories.forEach(cat => {
          if (cat.name.toLowerCase().includes(searchQuery.toLowerCase())) {
            matchingCategories.push({ ...cat, type: 'category' });
          }
          // Check subcategories
          cat.subcategories?.forEach(sub => {
            if (sub.name.toLowerCase().includes(searchQuery.toLowerCase())) {
              matchingCategories.push({ ...sub, type: 'subcategory', parentName: cat.name });
            }
          });
        });
        setCategorySuggestions(matchingCategories.slice(0, 3));
        
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error fetching suggestions:', err);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, categories, country]);

  // Location suggestions - free-text via Nominatim (OpenStreetMap) + always include "Near me"
  useEffect(() => {
    if (locationDebounceTimer.current) {
      clearTimeout(locationDebounceTimer.current);
    }

    // If user cleared input, just hide suggestions
    if (locationQuery.length < 2) {
      setLocationSuggestions([]);
      // Still allow opening dropdown on focus to show "Near me"
      return;
    }

    locationDebounceTimer.current = setTimeout(async () => {
      setLoadingLocSuggestions(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(locationQuery)}`;
        const res = await fetch(url, { headers: { 'Accept-Language': navigator.language || 'en' } });
        const data = await res.json();
        const places = (data || []).map((d) => ({
          label: d.display_name,
          short: d.address?.city || d.address?.town || d.address?.village || d.address?.county || d.address?.state || d.display_name.split(',')[0],
          lat: parseFloat(d.lat),
          lng: parseFloat(d.lon),
        }));
        setLocationSuggestions(places);
        setShowLocationSuggestions(true);
      } catch (e) {
        // Fallback to local SL cities if Nominatim fails
        const filtered = sriLankaLocations
          .filter((loc) => loc.toLowerCase().includes(locationQuery.toLowerCase()))
          .slice(0, 5)
          .map((name) => ({ label: name, short: name, lat: null, lng: null }));
        setLocationSuggestions(filtered);
        setShowLocationSuggestions(true);
      } finally {
        setLoadingLocSuggestions(false);
      }
    }, 350);

    return () => {
      if (locationDebounceTimer.current) {
        clearTimeout(locationDebounceTimer.current);
      }
    };
  }, [locationQuery]);

  // Trigger "Near me" - request browser geolocation, set state, AND navigate to /search immediately
  const handleNearMe = () => {
    console.log('[NearMe] button clicked');
    if (!navigator.geolocation) {
      alert('Geolocation is not supported in your browser.');
      return;
    }
    setNearMeLoading(true);
    setShowLocationSuggestions(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        console.log('[NearMe] got coords', latitude, longitude);
        setNearMe({ lat: latitude, lng: longitude, radius_km: 25 });
        setLocationQuery('Near me (25 km)');
        setNearMeLoading(false);

        // Auto-navigate to search results with geo params (best UX)
        const params = new URLSearchParams();
        if (searchQuery.trim()) params.set('q', searchQuery);
        params.set('lat', String(latitude));
        params.set('lng', String(longitude));
        params.set('radius_km', '25');
        navigate(`/search?${params.toString()}`);
      },
      (err) => {
        console.log('[NearMe] error', err);
        setNearMeLoading(false);
        alert(
          err.code === 1
            ? 'Location permission denied. Please allow location access to use "Near me".'
            : 'Unable to get your location. Please try again.'
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    setShowLocationSuggestions(false);
    
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery);

    if (nearMe && nearMe.lat != null && nearMe.lng != null) {
      // "Near me" mode - pass lat/lng + radius_km, skip text location
      params.set('lat', String(nearMe.lat));
      params.set('lng', String(nearMe.lng));
      params.set('radius_km', String(nearMe.radius_km || 25));
    } else if (locationQuery.trim()) {
      params.set('location', locationQuery);
    }
    
    if (params.toString()) {
      navigate(`/search?${params.toString()}`);
    }
  };

  const handleSuggestionClick = (listing) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/listing/${listing.id}`);
  };

  const handleCategorySuggestionClick = (category) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/category/${category.slug}`);
  };

  const handleLocationSelect = (location) => {
    // location is either a string (legacy fallback) or { label, short, lat, lng } object
    if (typeof location === 'string') {
      setLocationQuery(location);
      setNearMe(null);
    } else {
      setLocationQuery(location.short || location.label);
      // Free-text place - clear "near me" state; backend will regex-match the name
      setNearMe(null);
    }
    setShowLocationSuggestions(false);
  };

  // Clear "Near me" / location whenever user manually types
  const handleLocationChange = (e) => {
    setLocationQuery(e.target.value);
    if (nearMe) setNearMe(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Post Ad Button */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl notranslate" translate="no">Z</span>
              </div>
              <img 
                src="/zeilian-logo.png" 
                alt="Zeilian" 
                className="h-[50px] w-auto object-contain hidden sm:block"
              />
              <sup className="text-[9px] text-gray-400 font-semibold ml-0.5 hidden sm:inline">™</sup>
            </Link>
            
            {/* Post An Ad Button - Smaller & Compact */}
            <Link to="/post-ad" className="hidden md:flex">
              <Button className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full px-3 py-1 text-sm font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-1 h-8">
                <Plus className="h-3.5 w-3.5" />
                Post An Ad
              </Button>
            </Link>
          </div>

          {/* Split Search Bar - Desktop */}
          <form onSubmit={handleSearch} data-search-form className="hidden lg:flex flex-1 max-w-3xl mx-6">
            <div className="flex w-full bg-gray-50 rounded-full border-2 border-gray-200 focus-within:border-blue-500 transition-colors">
              {/* Search Input */}
              <div className="relative flex-1" ref={searchRef}>
                <div className="flex items-center h-11 px-4">
                  <Search className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <Input
                    type="text"
                    placeholder={isListening ? "🎤 Listening..." : "Search Zeilian"}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                    className="border-0 bg-transparent h-full p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
                  />
                  {/* Voice Search Button */}
                  {voiceSupported && (
                    <button
                      type="button"
                      onClick={toggleVoiceSearch}
                      className={`p-1.5 rounded-full transition-all ml-1 ${
                        isListening 
                          ? 'bg-red-100 text-red-600 animate-pulse' 
                          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                      title={isListening ? "Stop listening" : "Search by voice"}
                    >
                      {isListening ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && (suggestions.length > 0 || categorySuggestions.length > 0) && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border z-50 overflow-hidden min-w-[300px]">
                    {/* Category Suggestions */}
                    {categorySuggestions.length > 0 && (
                      <div className="p-2 border-b">
                        <p className="text-xs text-gray-500 px-3 py-1 font-medium">Categories</p>
                        {categorySuggestions.map((cat) => (
                          <div
                            key={cat.id}
                            className="flex items-center gap-3 p-2 hover:bg-blue-50 rounded-lg cursor-pointer"
                            onClick={() => handleCategorySuggestionClick(cat)}
                          >
                            <span className="text-lg">{cat.icon}</span>
                            <div>
                              <p className="font-medium text-sm">{cat.name}</p>
                              {cat.type === 'subcategory' && (
                                <p className="text-xs text-gray-500">in {cat.parentName}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Item Suggestions */}
                    {suggestions.length > 0 && (
                      <div className="p-2">
                        <p className="text-xs text-gray-500 px-3 py-1 font-medium">Items</p>
                        {suggestions.map((listing) => (
                          <div
                            key={listing.id}
                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                            onClick={() => handleSuggestionClick(listing)}
                          >
                            <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              {listing.images && listing.images.length > 0 ? (
                                <img
                                  src={listing.images[0].startsWith('http') ? listing.images[0] : `${API_URL}${listing.images[0]}`}
                                  alt={listing.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.src = 'https://via.placeholder.com/48'; }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Search className="h-3 w-3" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{listing.title}</p>
                              <p className="text-blue-600 text-xs font-semibold">
                                {formatCurrency(listing.price)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div
                      className="border-t p-2 bg-gray-50 text-center cursor-pointer hover:bg-gray-100"
                      onClick={handleSearch}
                    >
                      <p className="text-sm text-blue-600 font-medium">
                        See all results for "{searchQuery}"
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-px bg-gray-300 my-2"></div>

              {/* Location Input */}
              <div className="relative w-60" ref={locationRef}>
                <div className="flex items-center h-11 px-4">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <Input
                    type="text"
                    placeholder={isListeningLocation ? "🎤 Listening..." : "Location or near me"}
                    value={locationQuery}
                    onChange={handleLocationChange}
                    onFocus={() => setShowLocationSuggestions(true)}
                    className="border-0 bg-transparent h-full p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
                    data-testid="navbar-location-input"
                  />
                  {/* Clear button when "Near me" or any location is set */}
                  {(nearMe || locationQuery) && (
                    <button
                      type="button"
                      onClick={() => { setLocationQuery(''); setNearMe(null); setShowLocationSuggestions(false); }}
                      className="p-1 rounded-full text-gray-400 hover:text-gray-700 ml-1"
                      title="Clear location"
                      data-testid="navbar-location-clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {/* Voice Location Button */}
                  {voiceSupported && (
                    <button
                      type="button"
                      onClick={toggleLocationVoice}
                      className={`p-1.5 rounded-full transition-all ml-1 ${
                        isListeningLocation 
                          ? 'bg-red-100 text-red-600 animate-pulse' 
                          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                      title={isListeningLocation ? "Stop listening" : "Say location"}
                    >
                      {isListeningLocation ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>

                {/* Location Suggestions - always show "Near me" first */}
                {showLocationSuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border z-50 overflow-hidden">
                    <div className="p-2">
                      {/* Near Me option */}
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); handleNearMe(); }}
                        disabled={nearMeLoading}
                        className="w-full flex items-center gap-2 p-2 hover:bg-blue-50 rounded-lg text-left"
                        data-testid="navbar-near-me-btn"
                      >
                        <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                          {nearMeLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <LocateFixed className="h-3.5 w-3.5" />
                          )}
                        </span>
                        <span className="flex-1">
                          <span className="block text-sm font-semibold text-blue-700">
                            {nearMeLoading ? 'Getting your location…' : 'Near me'}
                          </span>
                          <span className="block text-[11px] text-gray-500">
                            Search within 25 km of your current location
                          </span>
                        </span>
                      </button>

                      {/* Divider when there are place suggestions */}
                      {(loadingLocSuggestions || locationSuggestions.length > 0) && (
                        <div className="border-t my-1.5" />
                      )}

                      {loadingLocSuggestions && (
                        <div className="px-2 py-2 text-xs text-gray-400 flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Searching locations…
                        </div>
                      )}

                      {!loadingLocSuggestions && locationSuggestions.map((loc, idx) => (
                        <div
                          key={`${loc.label}-${idx}`}
                          className="flex items-start gap-2 p-2 hover:bg-blue-50 rounded-lg cursor-pointer"
                          onMouseDown={(e) => { e.preventDefault(); handleLocationSelect(loc); }}
                          data-testid={`navbar-location-suggestion-${idx}`}
                        >
                          <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{loc.short}</p>
                            {loc.label !== loc.short && (
                              <p className="text-[11px] text-gray-500 truncate">{loc.label}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Button */}
              <Button
                type="submit"
                className="h-11 px-6 rounded-none rounded-r-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {loadingSuggestions ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
          </form>

          {/* Right Section */}
          <div className="flex items-center space-x-2">
            {/* Country Selector hidden - country is auto-detected by IP on first
                visit and persisted in localStorage. Users can still switch
                country from their account settings. */}

            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Notification Bell - Only for logged in users */}
            {isLoggedIn && <NotificationBell />}
            
            {isLoggedIn ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="user-menu" variant="ghost" size="icon" className="rounded-full">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-dropdown">
                    <div className="px-3 py-2" data-testid="user-menu-identity">
                      <p className="font-medium" data-testid="user-menu-name">{user?.name || 'User'}</p>
                      <p className="text-sm text-gray-500" data-testid="user-menu-email">{user?.email || 'user@example.com'}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      <Settings className="mr-2 h-4 w-4" />
                      My Ads
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')}>
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Messages
                      {unreadMessages > 0 && (
                        <Badge className="ml-auto bg-red-500 text-white text-xs">{unreadMessages}</Badge>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favorites')}>
                      <Heart className="mr-2 h-4 w-4" />
                      Favorites
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/saved-searches')}>
                      <Bookmark className="mr-2 h-4 w-4" />
                      Saved Searches
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/community')}>
                      <Users className="mr-2 h-4 w-4" />
                      Community Hub
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-orders')}>
                      <ShoppingBag className="mr-2 h-4 w-4" />
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/analytics')}>
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Analytics
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/membership')}>
                      <Settings className="mr-2 h-4 w-4" />
                      Membership
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full hidden sm:inline-flex">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full">
                    Register
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="menu"
              data-testid="mobile-menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Search - Split Design */}
        <div className="lg:hidden pb-3">
          <form onSubmit={handleSearch} data-search-form className="space-y-2">
            {/* Search Input with Voice */}
            <div className="relative" ref={searchRef}>
              <div className="flex items-center h-10 px-4 bg-gray-50 rounded-full border-2 border-gray-200 focus-within:border-blue-500">
                <Search className="h-4 w-4 text-gray-400 mr-2" />
                <Input
                  type="text"
                  placeholder={isListening ? "🎤 Listening..." : "Search Zeilian"}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-0 bg-transparent h-full p-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                  data-testid="mobile-search-input"
                />
                {/* Voice Search Button - Mobile */}
                {voiceSupported && (
                  <button
                    type="button"
                    onClick={toggleVoiceSearch}
                    className={`p-1.5 rounded-full transition-all ${
                      isListening 
                        ? 'bg-red-100 text-red-600 animate-pulse' 
                        : 'text-gray-400 hover:text-blue-600'
                    }`}
                  >
                    {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
            
            {/* Location + Search Row */}
            <div className="flex gap-2">
              <div className="relative flex-1" ref={locationRef}>
                <div className="flex items-center h-10 px-4 bg-gray-50 rounded-full border-2 border-gray-200 focus-within:border-blue-500">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                  <Input
                    type="text"
                    placeholder={isListeningLocation ? "🎤 Listening..." : "Location"}
                    value={locationQuery}
                    onChange={(e) => setLocationQuery(e.target.value)}
                    className="border-0 bg-transparent h-full p-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                  />
                  {/* Voice Location Button - Mobile */}
                  {voiceSupported && (
                    <button
                      type="button"
                      onClick={toggleLocationVoice}
                      className={`p-1.5 rounded-full transition-all ${
                        isListeningLocation 
                          ? 'bg-red-100 text-red-600 animate-pulse' 
                          : 'text-gray-400 hover:text-blue-600'
                      }`}
                    >
                      {isListeningLocation ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              </div>
              <Button
                type="submit"
                className="h-10 px-6 rounded-full bg-gradient-to-r from-blue-600 to-purple-600"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t bg-white" data-testid="mobile-nav" role="navigation">
          <div className="px-4 py-3 space-y-2">
            {isLoggedIn ? (
              <>
                <Link to="/post-ad" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Post Ad
                  </Button>
                </Link>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <Settings className="h-4 w-4 mr-2" />
                    My Ads
                  </Button>
                </Link>
                <Link to="/favorites" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <Heart className="h-4 w-4 mr-2" />
                    Favorites
                  </Button>
                </Link>
                <Link to="/saved-searches" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <Bookmark className="h-4 w-4 mr-2" />
                    Saved Searches
                  </Button>
                </Link>
                <Link to="/community" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <Users className="h-4 w-4 mr-2" />
                    Community Hub
                  </Button>
                </Link>
                <Link to="/my-orders" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    My Orders
                  </Button>
                </Link>
                <Link to="/messages" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Messages
                  </Button>
                </Link>
                <Button variant="ghost" className="w-full justify-start text-red-600" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full">
                    Login
                  </Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full">
                    Register
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
